import { useState } from 'react';
import { appointmentService } from '../../services/appointment';
import { toast } from '../../utils/toast';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

type Change = 'cancelled' | 'no-show' | 'scheduled';

const COPY: Record<Change, { title: string; description: string; confirm: string; done: string; reason: boolean }> = {
  cancelled: { title: 'Cancel this visit?', description: 'The appointment stays in the history as cancelled. You can put it back on the schedule later.', confirm: 'Cancel visit', done: 'Visit cancelled', reason: true },
  'no-show': { title: 'Mark as no-show?', description: 'Records that the patient did not come. You can put it back on the schedule later.', confirm: 'Mark no-show', done: 'Marked as no-show', reason: true },
  scheduled: { title: 'Put back on the schedule?', description: 'The visit becomes scheduled again and can be recorded.', confirm: 'Put back on schedule', done: 'Visit back on the schedule', reason: false },
};

interface VisitStatusDialogProps {
  appointmentId: string;
  change: Change;
  onClose: () => void;
  onChanged: () => void;
}

/** Cancel a booked visit, mark it as a no-show (with an optional reason), or reinstate it. */
export function VisitStatusDialog({ appointmentId, change, onClose, onChanged }: VisitStatusDialogProps) {
  const copy = COPY[change];
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    try {
      setSaving(true);
      await appointmentService.setVisitStatus(appointmentId, change, copy.reason ? reason.trim() || undefined : undefined);
      toast.success(copy.done);
      onChanged();
      onClose();
    } catch {
      // The API client shows why (for example, the visit was recorded meanwhile).
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>
        {copy.reason && (
          <div>
            <label htmlFor="visit-status-reason" className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
            <textarea id="visit-status-reason" className="input-field" rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
        )}
        <DialogFooter className="mt-4">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Keep as is</button>
          <button type="button" className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : copy.confirm}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
